import { useEffect, useState } from "react";
import { api, mxn, fmtDate, formatApiError } from "@/lib/api";
import { Modal, Btn, Select, Input } from "@/components/Kit";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const CONFIG = {
  remision: { title: "Remisión generada", endpoint: "remisiones", label: "REM" },
  factura: { title: "Factura generada", endpoint: "invoices", label: "FAC" },
  orden: { title: "Orden de trabajo generada", endpoint: "work-orders", label: "OT" },
};

export function ReviewModal({ open, type, doc, clients = [], products = [], onCreateProduct, onClose, onAccepted }) {
  const cfg = CONFIG[type] || CONFIG.remision;
  const isLineDoc = type === "remision" || type === "factura";

  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([]);
  const [ivaRate, setIvaRate] = useState(16);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!doc) return;
    setClientId(doc.client_id || "");
    const cleaned = (doc.items || []).filter((it) => (it.description || "").trim() || it.unit_price);
    setItems(cleaned.length ? cleaned : [{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(doc.iva_rate ?? 16);
    setNotes(doc.notes || "");
    setDueDate(doc.due_date || "");
    setTitle(doc.title || "");
    setDescription(doc.description || "");
    setAssignedTo(doc.assigned_to || "");
  }, [doc]);

  if (!doc) return null;

  const accept = async () => {
    setSaving(true);
    try {
      let payload;
      if (type === "remision") payload = { client_id: clientId, items, iva_rate: ivaRate, notes, authorize: true };
      else if (type === "factura") payload = { client_id: clientId, items, iva_rate: ivaRate, notes, due_date: dueDate, authorize: true };
      else payload = { title, description, assigned_to: assignedTo, due_date: dueDate, authorize: true };
      await api.put(`/${cfg.endpoint}/${doc.id}`, payload);
      toast.success(`${doc.number} aceptada`);
      onAccepted && onAccepted();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={cfg.title} wide>
      <div className="flex items-center gap-2 bg-[#465B7F]/10 border border-[#465B7F]/30 px-4 py-3 mb-5">
        <CheckCircle2 size={18} className="text-[#465B7F] shrink-0" />
        <p className="font-mono-data text-xs text-[#16202B]">
          Se generó <span className="font-semibold">{doc.number}</span>. Revisa y edita si es necesario, luego presiona
          <span className="font-semibold"> Aceptar</span> para autorizarla.
        </p>
      </div>

      <div className="space-y-5">
        {isLineDoc ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Select label="Cliente" value={clientId} onChange={(e) => setClientId(e.target.value)} testid="review-client-select">
                <option value="">Selecciona…</option>
                {[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })).map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
              </Select>
              {type === "factura" && (
                <Input label="Vencimiento" type="date" value={dueDate || ""} onChange={(e) => setDueDate(e.target.value)} testid="review-due-date" />
              )}
              {type === "remision" && (
                <div className="flex flex-col justify-end">
                  <span className="overline mb-1.5">Vence</span>
                  <span className="font-mono-data text-sm text-[#5B6672]">{fmtDate(doc.due_date)} (según crédito)</span>
                </div>
              )}
            </div>
            <LineItemsEditor items={items} setItems={setItems} ivaRate={ivaRate} setIvaRate={setIvaRate} products={products} onCreateProduct={onCreateProduct} />
            <label className="block">
              <span className="overline block mb-1.5">Notas</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="review-notes"
                className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
            </label>
          </>
        ) : (
          <>
            <Input label="Título" value={title} onChange={(e) => setTitle(e.target.value)} testid="review-title" />
            <label className="block">
              <span className="overline block mb-1.5">Descripción</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="review-description"
                className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Responsable" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} testid="review-assigned" />
              <Input label="Fecha entrega" type="date" value={dueDate || ""} onChange={(e) => setDueDate(e.target.value)} testid="review-order-due" />
            </div>
            {doc.items && doc.items.length > 0 && (
              <div className="border border-[#E3E8EE]">
                <div className="bg-[#EEF2F6] px-3 py-2 overline">Conceptos (referencia)</div>
                {doc.items.map((it, i) => (
                  <div key={i} className="flex justify-between px-3 py-2 border-t border-[#E3E8EE] font-mono-data text-sm">
                    <span>{it.description}</span>
                    <span className="text-[#5B6672]">{it.quantity} × {mxn(it.unit_price)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-3 border-t border-[#E3E8EE] pt-4">
          <Btn variant="ghost" type="button" onClick={onClose}>Cerrar sin aceptar</Btn>
          <Btn type="button" onClick={accept} disabled={saving} data-testid="accept-document-button">
            <CheckCircle2 size={16} /> {saving ? "Guardando…" : "Aceptar"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
